import "@mantine/core/styles.css";

import "@mantine/dates/styles.css";
import "@mantine/notifications/styles.css";
import "@mantine/spotlight/styles.css";
import "@mantine/nprogress/styles.css";

import { ReactElement, ReactNode, useMemo } from "react";

import { datadogRum } from "@datadog/browser-rum";
import { MantineProvider } from "@mantine/core";
import { MantineEmotionProvider, emotionTransform } from "@mantine/emotion";
import { Notifications } from "@mantine/notifications";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { mantineLightTheme } from "app/ui/theme";
import { NextPage } from "next";
import { AppProps } from "next/app";
import Head from "next/head";
import { useRouter } from "next/router";

import { TrackerAuthProvider } from "components/AuthProvider";
import ErrorBoundary from "components/ErrorBoundary";
import { BaseLayout } from "components/layouts/BaseLayout";
import { getDefaultLayout } from "components/layouts/default";
import { GaSnippet } from "components/lib/ga";
import { HeapSnippet } from "components/lib/heap";
import { BenchmarkingIntercomProvider } from "components/lib/intercom";
import { emotionCacheBenchmarking } from "emotion/cache";
import { cssVariablesResolver } from "ui/theme/cssVariablesResolver";
import logger from "utils/logger";
import { getCanonicalUrl, getBenchmarkingPageTitle } from "utils/utils";

import { en } from "../constants/locale/en";

import "app/lib/frontend/cm/index.scss";
import "components/google-one-tap/google-one-tap.scss";
import "components/slope-note-message-list/slope-note-message-list.scss";
import "components/text-editor/text-editor.scss";
import "components/text-editor/variable-list.scss";
import "components/nominal-percent-input/nominal-percent-input.scss";
import "components/ag-grid/ag-grid-cell-bonus-target-input/ag-grid-cell-bonus-target-adjustment-input.scss";
import "components/ag-grid/ag-grid-cell-icon-tooltip/ag-grid-cell-icon-tooltip.scss";
import "components/ag-grid/ag-grid-equity-grants-input/ag-grid-equity-grants-input.scss";
import "pages/reviews/[id]/setup/suggestions/_components/interval-editor.scss";
import "pages/reviews/[id]/analytics/pay-equity/_pay-equity.scss";
import "pages/employee-award-letter/employee-award-letter.scss";
import "app/lib/frontend/cm/components/manager-training/chat/chat.scss";

const BENCHMARK_SOCIAL_IMAGE_URL =
  "https://uploads-ssl.webflow.com/64c92a0b7a48fb18553ccdc8/64fa2a0ad8c9533d132806a2_comprehensive-social-share.png";

const NON_BENCHMARK_SOCIAL_IMAGE_URL =
  "https://cdn.prod.website-files.com/64c92a0b7a48fb18553ccdc8/64fa8993ecc183a3dd7a4a01_Comprehensive%20Social%20Share%20Image%202.png";

const NON_BENCHMARK_DESCRIPTION =
  "Compensation management software that integrates with your HR tools. Make efficient, transparent, and equitable compensation decisions without requiring a thousand spreadsheets.";
const NON_BENCHMARK_TITLE = "Comprehensive | Compensation Management Software";

if (typeof window !== "undefined" && !(window as any).DD_RUM) {
  datadogRum.init({
    applicationId: "019943a7-889b-475a-9d65-b235fc793ab0",
    clientToken: "pub49bda995e83e23274ba4c2e6dab9f183",
    site: "datadoghq.com",
    service: "benchmarking-frontend",
    env: process.env.NEXT_PUBLIC_RUNTIME_ENV ?? "unknown",
    version: process.env.NEXT_PUBLIC_RELEASE_VERSION,
    allowedTracingUrls: [
      /https:\/\/.*\.comprehensive\.io/,
      /https:\/\/.*cm-app-pr.*/,
      (url) => url.includes("comprehensive.io") || url.includes("cm-app-pr"),
    ],
    sampleRate: 100,
    sessionReplaySampleRate: 20,
    trackInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: "allow",
    beforeSend: (event) => {
      const isFromLocalhost = event.view.url.indexOf("localhost") !== -1;
      if (isFromLocalhost) {
        return false;
      }

      const isInternalToolsRoute = event.view.url.includes("internal-tools/cap");
      if (isInternalToolsRoute) {
        return false;
      }
      return true;
    },
  });

  (window as any).DD_RUM = datadogRum;
}

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};
type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

export function withBaseLayout(getLayout: (page: ReactElement) => ReactNode) {
  function BaseLayoutWrapper(page: ReactElement) {
    return <BaseLayout>{getLayout(page)}</BaseLayout>;
  }
  return BaseLayoutWrapper;
}

export default function TrackerApp({ Component, pageProps }: AppPropsWithLayout) {
  const getLayout = withBaseLayout(Component.getLayout ?? getDefaultLayout);
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            refetchOnReconnect: false,
            retry: false,
            // staleTime: twentyFourHoursInMs,
          },
        },
      }),
    [],
  );
  const router = useRouter();

  const pageTitle = useMemo(() => {
    let title;
    try {
      title = getBenchmarkingPageTitle(router, pageProps.results, pageProps.stats);
    } catch (e) {
      // @ts-expect-error TS2345
      logger.error("Error creating page title", e);
      title = en.translations.seo_title;
    }
    return title;
  }, [router, pageProps.results, pageProps.stats]);

  const canonicalUrl = useMemo(() => {
    let canonical = process.env.NEXT_PUBLIC_APP_URL ?? "https://comprehensive.io";
    try {
      canonical = getCanonicalUrl(router);
    } catch (e) {
      // @ts-expect-error TS2345
      logger.error("Error creating canonical URL", e);
    }
    return canonical;
  }, [router]);

  const pageDescription = useMemo(() => {
    let description;
    try {
      // description = getPageDescription(router, pageProps.results, pageProps.stats);
      description = en.translations.seo_description;
    } catch (e) {
      logger.error("Error creating page description");
      description = en.translations.seo_description;
    }
    return description;
  }, [router, pageProps.results, pageProps.stats]);

  const isBenchmarkingPage = useMemo(
    () => router.pathname.includes("/benchmarking"),
    [router.pathname],
  );

  const finalTitle = isBenchmarkingPage ? pageTitle : NON_BENCHMARK_TITLE;
  const finalDescription = isBenchmarkingPage ? pageDescription : NON_BENCHMARK_DESCRIPTION;
  const finalImageUrl = isBenchmarkingPage
    ? BENCHMARK_SOCIAL_IMAGE_URL
    : NON_BENCHMARK_SOCIAL_IMAGE_URL;

  return (
    <>
      <Head>
        <title>{finalTitle}</title>
        <meta charSet="utf-8" />
        <meta title={finalTitle} />

        <meta name="description" content={finalDescription} />

        <meta name="twitter:card" content="summary_large_image" />

        <meta name="twitter:image" content={finalImageUrl} />
        <meta name="twitter:title" content={finalTitle} />
        <meta name="twitter:url" content={canonicalUrl} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={finalTitle} />
        <meta property="og:image" content={finalImageUrl} />
        <meta
          name="viewport"
          content="width=device-width,initial-scale=1,minimum-scale=1.0,maximum-scale=1.0,user-scalable=no"
        />
        <meta name="facebook-domain-verification" content="bsz92905is1ems6vaz38rjojxxd2gu" />
        <meta property="og:type" content="website" />
        <meta property="og:description" content={finalDescription} />
        <meta name="twitter:description" content={finalDescription} />
        <link rel="icon" href="/favicon.ico" />
        <link rel="canonical" href={canonicalUrl} />

        <HeapSnippet heapAppId={process.env.NEXT_PUBLIC_HEAP_APP_ID} />
        <GaSnippet />
      </Head>
      <QueryClientProvider client={queryClient}>
        <TrackerAuthProvider>
          <BenchmarkingIntercomProvider appId={process.env.NEXT_PUBLIC_INTERCOM_APP_ID}>
            <MantineEmotionProvider cache={emotionCacheBenchmarking}>
              <MantineProvider
                theme={mantineLightTheme}
                stylesTransform={emotionTransform}
                cssVariablesResolver={cssVariablesResolver}
              >
                <ErrorBoundary datadogRum={datadogRum}>
                  <Notifications />
                  {getLayout(<Component {...pageProps} />)}
                </ErrorBoundary>
              </MantineProvider>
            </MantineEmotionProvider>
          </BenchmarkingIntercomProvider>
        </TrackerAuthProvider>
      </QueryClientProvider>
    </>
  );
}
